import {
	ChevronsUpDown,
	LogOut,
	LucideCookie,
	LucideFlaskConical,
	LucideLanguages,
	LucideSun,
} from "lucide-react";
import React from "react";
import { useAuth } from "react-oidc-context";
import * as CookieConsent from "vanilla-cookieconsent";
import ChangeLanguageDialog from "@/components/ChangeLanguageDialog";
import { ExperimentsDialogs } from "@/components/KultifyDrawer/ExperimentsDialogs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from "@/components/ui/sidebar";
import { m } from "@/paraglide/messages";
import { ModeToggle } from "./ModeToggle";

export function NavUser({
	user,
}: {
	user: {
		name: string;
		email: string;
		avatar: string | null | undefined;
	};
}) {
	const auth = useAuth();
	const { isMobile } = useSidebar();
	const [showExperiments, setShowExperiments] = React.useState(false);
	const [showChangeLanguage, setShowChangeLanguage] = React.useState(false);

	return (
		<>
			<ExperimentsDialogs
				open={showExperiments}
				onOpenChange={setShowExperiments}
			/>
			<ChangeLanguageDialog
				open={showChangeLanguage}
				onClose={() => setShowChangeLanguage(false)}
			/>

			<SidebarMenu>
				<SidebarMenuItem>
					<DropdownMenu>
						<DropdownMenuTrigger
							render={
								<SidebarMenuButton
									size="lg"
									className="data-[popup-open]:bg-sidebar-accent data-[popup-open]:text-sidebar-accent-foreground"
								>
									<UserInfo
										name={user.name}
										email={user.email}
										profileImage={user.avatar}
									/>

									<ChevronsUpDown className="ml-auto size-4" />
								</SidebarMenuButton>
							}
						/>
						<DropdownMenuContent
							className="w-(--anchor-width) min-w-56 rounded-lg"
							side={isMobile ? "bottom" : "right"}
							align="end"
							sideOffset={4}
						>
							<DropdownMenuGroup>
								<DropdownMenuLabel className="p-0 font-normal">
									<div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
										<UserInfo
											name={user.name}
											email={user.email}
											profileImage={user.avatar}
										/>
									</div>
								</DropdownMenuLabel>
							</DropdownMenuGroup>
							<DropdownMenuSeparator />
							<DropdownMenuGroup>
								<DropdownMenuItem onClick={() => setShowChangeLanguage(true)}>
									<LucideLanguages />
									{m.drab_quaint_okapi_empower()}
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={() => CookieConsent.showPreferences()}
								>
									<LucideCookie />
									{m.misty_livid_turtle_link()}
								</DropdownMenuItem>
								<ModeToggle>
									<LucideSun />
									{m.knotty_real_dog_type()}
								</ModeToggle>
							</DropdownMenuGroup>
							<DropdownMenuSeparator />

							<DropdownMenuGroup>
								<DropdownMenuItem onClick={() => setShowExperiments(true)}>
									<LucideFlaskConical />
									{m.zany_teary_mayfly_play()}
								</DropdownMenuItem>
							</DropdownMenuGroup>
							<DropdownMenuSeparator />
							<DropdownMenuItem onClick={() => auth.signoutRedirect()}>
								<LogOut />
								{m.wise_stout_kangaroo_pop()}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</SidebarMenuItem>
			</SidebarMenu>
		</>
	);
}

function UserInfo({
	name,
	email,
	profileImage,
}: {
	name: string;
	email: string;
	profileImage: string | null | undefined;
}) {
	const initiales = name
		.split(" ")
		.map((n) => n[0])
		.join("")
		.toUpperCase();
	return (
		<>
			<Avatar className="h-8 w-8 rounded-lg">
				<AvatarImage src={profileImage ?? undefined} alt={name} />
				<AvatarFallback className="rounded-full">{initiales}</AvatarFallback>
			</Avatar>
			<div className="grid flex-1 text-left text-sm leading-tight">
				<span className="truncate font-medium">{name}</span>
				<span className="truncate text-xs">{email}</span>
			</div>
		</>
	);
}
